import { useRef, useState, useEffect } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useUploadDialog } from "./UploadDialogProvider";
import { useProjectFileUploadStarter } from "../Assets/lib/hooks/useProjectFileUploadStarter";
import { classes } from "lib/actions/util";
import { useSupportedFileExtensions } from "lib/hooks/useSupportedFileExtensions";
import FileAddSvg from "components/icons/24/actions/file-add.svg";
import SpeckleLogoSvg from "./speckle-logo.svg";
import Modal from "components/elements/Dialog/Modal";
import Tabs from "components/elements/Tabs/Tabs";
import Tab from "components/elements/Tabs/Tab";
import TabContent from "components/elements/Tabs/TabContent";
import SmartPanelTabBar from "components/elements/SidePanel/SidePanelTabBar";
import FileBrowser, {
  FileBrowserInterface,
} from "components/elements/FileBrowser/FileBrowser";
import FileDropzone from "components/elements/Upload/FileDropzone";
import SpeckleWizard from "./SpeckleImportWizard/SpeckleImportWizard";
import SpeckleIntroduction from "./SpeckleIntroduction";
import UploadSourceChooser from "./UploadSourceChooser";
import AutodeskImportModal from "./AutodeskImport/AutodeskImportModal";
import Button from "components/elements/Button/Button";

export default function UploadDialog() {
  const [isSpeckleImportOpen, setSpeckleImportOpen] = useState(false);
  const [isSpeckleInfoExpanded, setSpeckleInfoExpanded] = useState(false);
  const [isAutodeskImportOpen, setAutodeskImportOpen] = useState(false);
  const {
    moduleSpeckle: hasSpeckle,
    featureAutodeskConnector: hasAutodeskConnector,
  } = useFlags();
  const fileBrowser = useRef<FileBrowserInterface>(null);
  const { isOpen: isUploadDialogOpen, close: closeUploadDialog } =
    useUploadDialog();
  const { startFiles } = useProjectFileUploadStarter();
  const supportedFileExtensions = useSupportedFileExtensions();

  function onBrowse() {
    fileBrowser.current.open();
  }

  function onClose() {
    closeUploadDialog();
    setSpeckleInfoExpanded(false);
  }

  function onImportOpen() {
    setSpeckleImportOpen(true);
    setSpeckleInfoExpanded(false);
  }

  function onImportClose() {
    setSpeckleImportOpen(false);
    onClose();
  }

  function onAutodeskImportOpen() {
    setAutodeskImportOpen(true);
  }

  function onAutodeskImportClose() {
    setAutodeskImportOpen(false);
    onClose();
  }

  function onFileSelect(files: File[]) {
    startFiles(files);
    onClose();
  }

  useEffect(() => {
    if (isUploadDialogOpen) return;
    setSpeckleImportOpen(false);
    setAutodeskImportOpen(false);
  }, [isUploadDialogOpen]);

  const speckleImportModal = (
    <Modal
      isOpen={isUploadDialogOpen && isSpeckleImportOpen}
      onClose={onImportClose}
      disableSizing
      className="w-full overflow-hidden sm:max-w-4xl"
    >
      <SpeckleWizard />
    </Modal>
  );

  const fileBrowserElement = (
    <FileBrowser
      ref={fileBrowser}
      multi
      accept={supportedFileExtensions}
      onSelected={onFileSelect}
    />
  );

  if (hasAutodeskConnector)
    return (
      <>
        {speckleImportModal}
        <AutodeskImportModal
          isOpen={isUploadDialogOpen && isAutodeskImportOpen}
          onClose={onAutodeskImportClose}
        />
        <Modal
          isOpen={isUploadDialogOpen}
          onClose={onClose}
          disableSizing
          className="w-full p-6 sm:max-w-[56rem]"
        >
          <UploadSourceChooser
            hasSpeckle={hasSpeckle}
            onBrowse={onBrowse}
            onAddFiles={onFileSelect}
            onSpeckleImport={onImportOpen}
            onAutodeskImport={onAutodeskImportOpen}
          />
          {fileBrowserElement}
        </Modal>
      </>
    );

  return (
    <>
      {speckleImportModal}
      <Modal isOpen={isUploadDialogOpen} onClose={onClose} className="p-4">
        <h1 className="mb-3 text-lg">Add Assets</h1>
        <Tabs>
          <SmartPanelTabBar className="mb-5">
            <Tab onActivate={() => setSpeckleInfoExpanded(false)} initial>
              Upload
            </Tab>
            <Tab className={classes(!hasSpeckle && "hidden")}>
              <SpeckleLogoSvg className="mr-2 inline w-4" />
              <span className="font-medium text-blue-700">Speckle</span>
            </Tab>
          </SmartPanelTabBar>
          <TabContent>
            <FileDropzone onAddFiles={onFileSelect}>
              <section className="rounded-lg border-2 border-dotted border-gray-300 p-12 text-center">
                <FileAddSvg className="m-auto mb-6 w-16 text-gray-400" />
                <div className="mb-2 text-sm">
                  <p>Drag & drop files here</p>
                  <p>or</p>
                </div>
                <Button
                  theme="primary-outline"
                  slim
                  onClick={onBrowse}
                  className="mx-auto"
                >
                  Upload from device
                </Button>
              </section>
            </FileDropzone>
          </TabContent>
          <TabContent>
            <section className="rounded-lg bg-stone-50 p-6 pb-10 text-sm">
              <SpeckleIntroduction
                isExpanded={isSpeckleInfoExpanded}
                onExpand={() => setSpeckleInfoExpanded(true)}
              />
              <Button
                theme="primary-outline"
                type="button"
                className="mx-auto text-base"
                onClick={onImportOpen}
              >
                Import model
              </Button>
            </section>
          </TabContent>
        </Tabs>

        {fileBrowserElement}
      </Modal>
    </>
  );
}
